/**
 * Nested Tabs Card Overlay Functionality
 * Handles opening and closing of overlay modals when cards are clicked
 * Also handles AJAX loading of Aim content and overlay content
 */

/* global nestedTabsAjax, FormData */

(function () {
	'use strict';

	// Check if AJAX object is available
	if (typeof nestedTabsAjax === 'undefined') {
		return;
	}

	const { ajax_url: ajaxUrl, nonce } = nestedTabsAjax;

	// Function to open overlay
	function openOverlay(overlayId) {
		const overlay = document.getElementById(overlayId);
		if (!overlay) return;

		// Immediately show the overlay
		overlay.setAttribute('aria-hidden', 'false');
		overlay.classList.add('nested-tabs__overlay--active');
		document.body.style.overflow = 'hidden';

		// Check if overlay content is already loaded in the inner div
		const overlayBodyInner = overlay.querySelector(
			'.nested-tabs__overlay-body--inner'
		);

		// If content doesn't exist or is empty, show loading state and load via AJAX
		if (!overlayBodyInner || overlayBodyInner.innerHTML.trim() === '') {
			// Show loading state immediately in the inner div
			if (overlayBodyInner) {
				overlayBodyInner.innerHTML =
					'<div class="nested-tabs__overlay-loading"><div class="nested-tabs__overlay-spinner"></div></div>';
			}

			// Get card and post ID
			const card = document.querySelector(
				`[data-overlay="${overlayId.replace('overlay-', '')}"]`
			);
			if (card) {
				const postId = card.getAttribute('data-post-id');
				if (postId) {
					// Defer AJAX call to next tick to ensure overlay is visible first
					setTimeout(() => {
						loadOverlayContent(postId, overlay);
					}, 0);
				}
			}
		} else {
			// Content exists, focus close button
			const closeButton = overlay.querySelector(
				'.nested-tabs__overlay-close'
			);
			if (closeButton) {
				closeButton.focus();
			}
		}
	}

	// Function to close overlay
	function closeOverlay(overlay) {
		if (!overlay) return;

		overlay.setAttribute('aria-hidden', 'true');
		overlay.classList.remove('nested-tabs__overlay--active');

		const activeOverlays = document.querySelectorAll(
			'.nested-tabs__overlay--active'
		);
		if (activeOverlays.length === 0) {
			document.body.style.overflow = '';
			document.body.style.paddingRight = '';
		}
	}

	// Load overlay content via AJAX
	function loadOverlayContent(postId, overlayElement) {
		if (!overlayElement) return;

		const overlayBodyInner = overlayElement.querySelector(
			'.nested-tabs__overlay-body--inner'
		);
		if (!overlayBodyInner) return;

		const formData = new FormData();
		formData.append('action', 'nested_tabs_overlay');
		formData.append('nonce', nonce);
		formData.append('post_id', postId);

		fetch(ajaxUrl, {
			method: 'POST',
			body: formData,
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.success && data.data.html) {
					// Load content into the inner div
					overlayBodyInner.innerHTML = data.data.html;
					// Re-bind close handlers
					initOverlayHandlers();
					// Focus close button
					const closeButton = overlayElement.querySelector(
						'.nested-tabs__overlay-close'
					);
					if (closeButton) {
						closeButton.focus();
					}
				} else {
					overlayBodyInner.innerHTML =
						'<div class="nested-tabs__overlay-error">Error loading content.</div>';
				}
			})
			.catch(() => {
				overlayBodyInner.innerHTML =
					'<div class="nested-tabs__overlay-error">Error loading content.</div>';
			});
	}

	// Load Aim content via AJAX
	function loadAimContent(aimSlug) {
		const nestedTabs = document.querySelector('.nested-tabs');
		if (!nestedTabs) return;

		const contentInner = nestedTabs.querySelector(
			'.nested-tabs__content-inner'
		);
		if (!contentInner) return;

		const loading = nestedTabs.querySelector('.nested-tabs__loading');

		// Show loading state
		if (loading) {
			loading.style.display = 'block';
		}
		contentInner.style.opacity = '0.5';

		const formData = new FormData();
		formData.append('action', 'nested_tabs_aim_content');
		formData.append('nonce', nonce);
		formData.append('aim_slug', aimSlug);

		fetch(ajaxUrl, {
			method: 'POST',
			body: formData,
		})
			.then((response) => response.json())
			.then((data) => {
				if (loading) {
					loading.style.display = 'none';
				}
				contentInner.style.opacity = '1';

				if (data.success && data.data.html) {
					contentInner.innerHTML = data.data.html;
					// Re-initialize overlay functionality for new content
					initNestedTabsOverlay();
					// Re-initialize primary driver tabs for new content
					initPrimaryDriverTabs();
					// Re-apply match height after content loads
					matchColumnHeadersHeight();
				} else {
					contentInner.innerHTML =
						'<div class="nested-tabs__error">Error loading content.</div>';
				}
			})
			.catch(() => {
				if (loading) {
					loading.style.display = 'none';
				}
				contentInner.style.opacity = '1';
				contentInner.innerHTML =
					'<div class="nested-tabs__error">Error loading content.</div>';
			});
	}

	// Initialize overlay handlers
	function initOverlayHandlers() {
		const closeButtons = document.querySelectorAll(
			'.nested-tabs__overlay-close'
		);
		const backdrops = document.querySelectorAll(
			'.nested-tabs__overlay-backdrop'
		);

		// Handle close button clicks
		closeButtons.forEach((button) => {
			// Remove existing listeners by cloning
			const newButton = button.cloneNode(true);
			button.parentNode.replaceChild(newButton, button);

			newButton.addEventListener('click', function (e) {
				e.preventDefault();
				e.stopPropagation();
				const overlay = newButton.closest('.nested-tabs__overlay');
				closeOverlay(overlay);
			});
		});

		// Handle backdrop clicks
		backdrops.forEach((backdrop) => {
			// Remove existing listeners by cloning
			const newBackdrop = backdrop.cloneNode(true);
			backdrop.parentNode.replaceChild(newBackdrop, backdrop);

			newBackdrop.addEventListener('click', function (e) {
				e.preventDefault();
				e.stopPropagation();
				const overlay = newBackdrop.closest('.nested-tabs__overlay');
				closeOverlay(overlay);
			});
		});
	}

	// Initialize nested tabs functionality
	function initNestedTabsOverlay() {
		const cards = document.querySelectorAll(
			'.nested-tabs__card[data-overlay]'
		);

		// Handle card clicks - make entire card clickable
		cards.forEach((card) => {
			// Remove existing listeners by cloning
			const newCard = card.cloneNode(true);
			card.parentNode.replaceChild(newCard, card);

			const overlayId = newCard.getAttribute('data-overlay');
			if (!overlayId) return;

			const targetOverlayId = `overlay-${overlayId}`;

			// Add click handler to entire card
			newCard.addEventListener('click', function (e) {
				// Don't open overlay if toggle button was clicked
				if (e.target.closest('.nested-tabs__card-toggle')) {
					return;
				}
				openOverlay(targetOverlayId);
			});

			// Also handle toggle button clicks
			const toggleButton = newCard.querySelector(
				'.nested-tabs__card-toggle'
			);
			if (toggleButton) {
				toggleButton.addEventListener('click', function (e) {
					e.preventDefault();
					e.stopPropagation();
					openOverlay(targetOverlayId);
				});
			}
		});

		// Initialize overlay handlers
		initOverlayHandlers();
	}
	// Update sub-wrap class based on active primary driver color
	function updateSubWrapClass(nestedTabs) {
		if (!nestedTabs) return;

		const activeLink = nestedTabs.querySelector(
			'.nested-tabs__secondary-item--active a[data-color]'
		);
		if (!activeLink) return;

		const subWrap = nestedTabs.querySelector('.nested-tabs__sub-wrap');
		if (!subWrap) return;

		const color = activeLink.getAttribute('data-color');

		// Remove all color classes
		const colorClasses = Array.from(subWrap.classList).filter((cls) =>
			cls.startsWith('nested-tabs__sub-wrap--')
		);
		colorClasses.forEach((cls) => subWrap.classList.remove(cls));

		// Add new color class if color exists
		if (color) {
			subWrap.classList.add(`nested-tabs__sub-wrap--${color}`);
		}
	}

	// Initialize Primary Driver tabs
	function initPrimaryDriverTabs() {
		const nestedTabs = document.querySelector('.nested-tabs');
		if (!nestedTabs) return;

		const primaryDriverLinks = nestedTabs.querySelectorAll(
			'.nested-tabs__secondary-nav a[data-primary-driver-id]'
		);
		primaryDriverLinks.forEach((link) => {
			// Remove existing listeners by cloning
			const newLink = link.cloneNode(true);
			link.parentNode.replaceChild(newLink, link);

			newLink.addEventListener('click', function (e) {
				e.preventDefault();
				const navItem = newLink.closest('.nested-tabs__secondary-item');
				const secondaryContent = newLink
					.closest('.nested-tabs__sub-wrap')
					.querySelector('.nested-tabs__secondary-content');

				if (!navItem || !secondaryContent) return;

				const primarySlug = newLink
					.getAttribute('href')
					.replace('#', '');

				// Update active nav item
				const allNavItems = newLink
					.closest('.nested-tabs__secondary-nav')
					.querySelectorAll('.nested-tabs__secondary-item');
				allNavItems.forEach((item) => {
					item.classList.remove(
						'nested-tabs__secondary-item--active'
					);
				});
				navItem.classList.add('nested-tabs__secondary-item--active');

				// Show/hide panels
				const allPanels = secondaryContent.querySelectorAll(
					'.nested-tabs__secondary-panel'
				);
				const targetPanel = secondaryContent.querySelector(
					`#${primarySlug}-panel`
				);

				allPanels.forEach((panel) => {
					panel.style.display = 'none';
					panel.classList.remove(
						'nested-tabs__secondary-panel--active'
					);
				});

				if (targetPanel) {
					targetPanel.style.display = '';
					targetPanel.classList.add(
						'nested-tabs__secondary-panel--active'
					);
					// Re-apply match height when panel is shown
					matchColumnHeadersHeight();
				}

				// Update sub-wrap class based on active primary driver color
				updateSubWrapClass(nestedTabs);
			});
		});

		// Set initial class based on first active primary driver
		updateSubWrapClass(nestedTabs);
	}

	// Handle URL hash changes
	function handleHashChange() {
		const nestedTabs = document.querySelector('.nested-tabs');
		if (!nestedTabs) return;

		const hash = window.location.hash.replace('#', '');

		// If no hash, ensure default content is visible (already rendered server-side)
		if (!hash) {
			const currentAim = nestedTabs.getAttribute('data-active-aim');
			// Content should already be visible, just ensure first nav item is active
			const firstNavItem = nestedTabs.querySelector(
				'.nested-tabs__nav-item'
			);
			if (firstNavItem) {
				const allNavItems = nestedTabs.querySelectorAll(
					'.nested-tabs__nav-item'
				);
				allNavItems.forEach((item) =>
					item.classList.remove('nested-tabs__nav-item--active')
				);
				firstNavItem.classList.add('nested-tabs__nav-item--active');
			}
			// Ensure the first panel is visible
			if (currentAim) {
				const firstPanel = nestedTabs.querySelector(
					`#tab-${currentAim}`
				);
				if (firstPanel) {
					const allPanels = nestedTabs.querySelectorAll(
						'.nested-tabs__panel'
					);
					allPanels.forEach((p) => {
						if (p.id !== firstPanel.id) {
							p.style.display = 'none';
						} else {
							p.style.display = '';
							p.classList.add('nested-tabs__panel--active');
						}
					});
				}
			}
			return;
		}

		// Check if this is an Aim slug
		const aimLink = nestedTabs.querySelector(`[data-aim-slug="${hash}"]`);
		if (aimLink) {
			const aimSlug = aimLink.getAttribute('data-aim-slug');
			const currentActive = nestedTabs.querySelector(
				'.nested-tabs__nav-item--active'
			);
			const targetNavItem = aimLink.closest('.nested-tabs__nav-item');

			// Update active nav item
			if (currentActive) {
				currentActive.classList.remove('nested-tabs__nav-item--active');
			}
			if (targetNavItem) {
				targetNavItem.classList.add('nested-tabs__nav-item--active');
			}

			// Load content if different from current
			const currentAim = nestedTabs.getAttribute('data-active-aim');
			if (currentAim !== aimSlug) {
				nestedTabs.setAttribute('data-active-aim', aimSlug);
				loadAimContent(aimSlug);
			} else {
				// Content is already loaded, just ensure it's visible
				const panel = nestedTabs.querySelector(`#tab-${aimSlug}`);
				if (panel) {
					// Hide all panels and show this one
					const allPanels = nestedTabs.querySelectorAll(
						'.nested-tabs__panel'
					);
					allPanels.forEach((p) => {
						p.style.display = 'none';
						p.classList.remove('nested-tabs__panel--active');
					});
					panel.style.display = 'block';
					panel.classList.add('nested-tabs__panel--active');
				}
			}
		}
	}

	// Match heights of column headers
	function matchColumnHeadersHeight() {
		const columnHeaders = document.querySelectorAll(
			'.nested-tabs__column-header'
		);
		if (columnHeaders.length === 0) return;

		// Reset heights to auto to recalculate
		columnHeaders.forEach((header) => {
			header.style.height = 'auto';
		});

		// Group headers by row based on their top position
		const rows = [];
		const tolerance = 1; // pixels tolerance for grouping

		columnHeaders.forEach((header) => {
			const rect = header.getBoundingClientRect();
			const top = rect.top + window.scrollY;

			// Find if this header belongs to an existing row
			let foundRow = false;
			for (let i = 0; i < rows.length; i++) {
				if (Math.abs(rows[i].top - top) <= tolerance) {
					rows[i].headers.push(header);
					foundRow = true;
					break;
				}
			}

			// If no matching row found, create a new one
			if (!foundRow) {
				rows.push({
					top,
					headers: [header],
				});
			}
		});

		// Apply matching heights to each row
		rows.forEach((row) => {
			if (row.headers.length <= 1) return; // Skip rows with only one element

			// Find the maximum height in this row
			let maxHeight = 0;
			row.headers.forEach((header) => {
				// Temporarily set height to auto to get natural height
				header.style.height = 'auto';
				const height = header.offsetHeight;
				if (height > maxHeight) {
					maxHeight = height;
				}
			});

			// Apply max height to all headers in this row
			if (maxHeight > 0) {
				row.headers.forEach((header) => {
					header.style.height = `${maxHeight}px`;
				});
			}
		});
	}

	// Initialize on page load
	function init() {
		// Initialize overlay functionality
		initNestedTabsOverlay();

		// Initialize Primary Driver tabs
		initPrimaryDriverTabs();

		// Match column header heights
		matchColumnHeadersHeight();

		// Handle initial hash (or lack thereof)
		handleHashChange();

		// Handle hash changes
		window.addEventListener('hashchange', handleHashChange);

		// Handle navigation clicks
		const nestedTabs = document.querySelector('.nested-tabs');
		if (nestedTabs) {
			const navLinks = nestedTabs.querySelectorAll(
				'.nested-tabs__nav-item a[data-aim-slug]'
			);
			navLinks.forEach((link) => {
				link.addEventListener('click', function (e) {
					const aimSlug = link.getAttribute('data-aim-slug');
					if (aimSlug) {
						// Update URL hash
						window.location.hash = aimSlug;
						// Prevent default to handle via hash change
						e.preventDefault();
					}
				});
			});
		}

		// Handle ESC key to close overlay
		document.addEventListener('keydown', function (e) {
			if (e.key === 'Escape') {
				const activeOverlay = document.querySelector(
					'.nested-tabs__overlay--active'
				);
				if (activeOverlay) {
					closeOverlay(activeOverlay);
				}
			}
		});
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	// Re-apply match height on window resize
	window.addEventListener('resize', matchColumnHeadersHeight);
})();
